import { Howl } from "howler";
import { sentryCapture } from "sentry/sentry";

type PlayAudioOptions = {
  src: string;
  onStarted?: (sound: Howl) => void;
  onEnded?: () => void;
};

const addHowlListeners = (
  sound: Howl,
  { onStarted, onEnded }: { onStarted?: (sound: Howl) => void; onEnded?: () => void }
) => {
  sound.on("play", () => onStarted?.(sound));
  sound.on("end", () => onEnded?.());
  sound.on("playerror", () => onEnded?.());
  sound.on("stop", () => onEnded?.());
  sound.on("loaderror", () => onEnded?.());
};

const removeHowlListeners = (sound: Howl) => {
  sound.off("play");
  sound.off("end");
  sound.off("playerror");
  sound.off("stop");
  sound.off("loaderror");
};

let lastSound: { sound?: Howl; audioSrc?: string } | null = null;

export const playAudio = ({ src, onStarted, onEnded }: PlayAudioOptions) => {
  try {
    const audioSrc = src.startsWith("http") ? src : `data:audio/wav;base64,${src}`;

    let sound: Howl | undefined =
      lastSound?.audioSrc === audioSrc
        ? lastSound?.sound
        : new Howl({
            src: [audioSrc],
            format: ["wav"],
            html5: true
          });

    if (!sound) throw new Error("Failed to create audio instance");

    const handleOnStarted = (sound: Howl) => {
      onStarted?.(sound);
      // unloading after starting next sound
      //  - this approach keeps reference to soundcontext so it can play on bg ios and older androids
      //  - important is that unload is called after next started!
      if (lastSound && lastSound.audioSrc !== audioSrc) {
        lastSound.sound?.unload();
      }
      lastSound = { sound, audioSrc: audioSrc };
    };

    const handleOnEnded = () => {
      onEnded?.();
      sound && removeHowlListeners(sound);
    };

    addHowlListeners(sound, { onStarted: handleOnStarted, onEnded: handleOnEnded });
    sound.play();
  } catch (e) {
    onEnded?.();
    sentryCapture("Error playing audio: ", { e });
    return () => {};
  }
};
